// @ts-check

/**
 * Inclusive bounds for operator number fields.
 *
 * `default` is the number a fresh install uses. It is never implied by storing 0.
 * A saved value below `min` or above `max` is clamped. 0 is only kept when `min` is 0.
 *
 * @type {Record<string, { min: number, max: number, default: number }>}
 */
export const NUMERIC_SETTING_BOUNDS = {
  max_active_rooms: { min: 1, max: 10000, default: 1000 },
  max_rooms_per_user: { min: 1, max: 100, default: 10 },
  max_players_per_room: { min: 1, max: 500, default: 50 },
  host_idle_seconds: { min: 5, max: 3600, default: 60 },
  host_disconnect_grace_seconds: { min: 0, max: 300, default: 10 },
  piece_hold_timeout_seconds: { min: 1, max: 600, default: 15 },
  max_multi_select_stack: { min: 1, max: 100, default: 10 },
  movement_smoothing_ms: { min: 0, max: 1000, default: 200 },
  room_history_days: { min: 1, max: 365, default: 14 },
  room_abandon_hours: { min: 1, max: 8760, default: 48 },
  room_soft_expire_days: { min: 1, max: 365, default: 14 },
  empty_room_expire_seconds: { min: 30, max: 86400, default: 300 },
  room_hard_delete_days: { min: 30, max: 3650, default: 60 },
  web_user_purge_days: { min: 1, max: 3650, default: 90 },
  discord_user_purge_days: { min: 1, max: 3650, default: 365 },
  user_session_ttl_days: { min: 1, max: 365, default: 7 },
  admin_session_ttl_hours: { min: 1, max: 168, default: 12 },
  client_error_retention_days: { min: 1, max: 3650, default: 30 },
  chat_max_length: { min: 1, max: 4000, default: 500 },
  chat_rate_per_10s: { min: 1, max: 100, default: 5 },
  chat_history_size: { min: 1, max: 1000, default: 100 },
  image_max_upload_mb: { min: 1, max: 100, default: 20 },
  image_min_width: { min: 1, max: 10000, default: 1200 },
  image_min_height: { min: 1, max: 10000, default: 800 },
  bot_next_puzzle_delay_seconds: { min: 0, max: 3600, default: 30 },
  bot_host_idle_seconds: { min: 1, max: 3600, default: 30 },
  bot_host_pick_window_seconds: { min: 10, max: 600, default: 15 },
  bot_max_idle_puzzles: { min: 1, max: 50, default: 2 },
  bot_min_pieces: { min: 0, max: 1000, default: 0 },
  bot_max_pieces: { min: 0, max: 1000, default: 0 },
  bot_max_channels_per_guild: { min: 1, max: 100, default: 1 },
  bot_lease_ttl_seconds: { min: 10, max: 86400, default: 120 },
  public_rooms_query_limit: { min: 1, max: 100, default: 40 },
  bot_recent_image_memory: { min: 1, max: 100, default: 10 },
  bot_candidate_limit: { min: 10, max: 500, default: 60 },
  // 0 leaves each route on its own budget. It is not a stand-in for one shared number.
  rate_limit_strict_per_10min: { min: 0, max: 100000, default: 0 },
  rate_limit_standard_per_10min: { min: 0, max: 100000, default: 0 },
  rate_limit_media_per_10min: { min: 0, max: 100000, default: 0 }
};

/**
 * Clamps a stored number into its field bounds.
 * A blank or non-numeric value uses `fallback`, then the field default.
 *
 * @param {string} key
 * @param {unknown} value
 * @param {number} [fallback]
 * @returns {number}
 */
export function clampNumericSetting(key, value, fallback) {
  const bounds = NUMERIC_SETTING_BOUNDS[key];
  const fb = Number.isFinite(fallback)
    ? /** @type {number} */ (fallback)
    : (bounds ? bounds.default : 0);
  const raw = value == null ? '' : String(value).trim();
  const n = raw === '' ? NaN : Number(raw);
  const base = Number.isFinite(n) ? Math.floor(n) : Math.floor(fb);
  if (!bounds) return Math.max(0, base);
  return Math.min(bounds.max, Math.max(bounds.min, base));
}

/**
 * Hint shown beside an admin number field.
 *
 * @param {string} key
 * @returns {string}
 */
export function numericSettingHint(key) {
  const bounds = NUMERIC_SETTING_BOUNDS[key];
  if (!bounds) return '';
  return `Minimum ${bounds.min}. Maximum ${bounds.max}. Default ${bounds.default}.`;
}
